import type { RuntimeConfig } from "nuxt/schema";
import type {
  AuthLoginOptions,
  AuthFlow,
  AuthAccount,
  AuthEventMessage,
  AuthEventCallback,
  AuthResponse,
} from "../types/providers";

export default abstract class BaseAuthClass {
  config: RuntimeConfig;
  #eventCallbacks = new Set<AuthEventCallback>();

  constructor(config: RuntimeConfig) {
    this.config = config;
  }
  abstract get authFlow(): AuthFlow;
  abstract get authValidUntil(): number | undefined;
  abstract initialize(): Promise<void>;
  abstract login(options?: AuthLoginOptions): Promise<boolean>;
  abstract logout(): Promise<boolean>;
  abstract refreshToken(givenToken?: string): Promise<string>;
  abstract getToken(): Promise<string>;
  abstract getAccounts(): Promise<AuthAccount[]>;
  abstract isAuthed(): Promise<boolean>;
  abstract clearData(): Promise<void>;
  abstract resetAuthFlow(): Promise<void>;

  /**
   * This method should handle any errors thrown by the current auth provider.
   *
   * @param error - Error thrown by the auth system
   * @returns True if error is handled, False if auth should be cleared.
   */
  abstract handleAuthError(error: Error): Promise<boolean>;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resetPassword(payload: { token: string; newPassword: string }): Promise<AuthResponse> {
    throw new Error("Not implemented");
  }

  get isMultiTenant() {
    return $ck.utils.parseBoolean(this.config.public.MULTI_TENANT);
  }

  isExpired() {
    // if there isn't a date, use now, so we can assume things have expired.
    if (!this.authValidUntil) return true;

    // Give ourselves a minute of leeway time to refresh the auth token
    const now = Date.now() - $ck.utils.TimeInMS.ONE_MINUTE;
    return this.authValidUntil <= now;
  }

  addEventCallback(callback: AuthEventCallback) {
    this.#eventCallbacks.add(callback);
  }

  removeEventCallback(callback: AuthEventCallback) {
    this.#eventCallbacks.delete(callback);
  }

  clearEventCallbacks() {
    this.#eventCallbacks.clear();
  }

  broadcastEvent(message: AuthEventMessage) {
    for (const callback of this.#eventCallbacks) {
      try {
        callback(message);
      } catch (error) {
        console.error("Error in event callback:", error);
      }
    }
  }
}
